// Copyright (c) Docugami, Inc. All rights reserved.

import { BaseQueryApi, QueryReturnValue } from "@reduxjs/toolkit/dist/query/baseQueryTypes";
import { createApi, FetchBaseQueryError } from "@reduxjs/toolkit/dist/query/react";
import { Config } from "config";
import { OrganizationFeatures } from "networking/api";
import type { AppDispatch, AppState } from "store";
import { createDgBaseQuery } from "store/baseQuery";
import { getIsFirstArrival, setFirstArrivalComplete } from "utils/localStorageHelpers";

const workspaceBaseQuery = createDgBaseQuery(`${Config.workspaceApiUrl}`);
export const organizationApiSlice = createApi({
    reducerPath: 'organizationApi',
    baseQuery: workspaceBaseQuery,
    tagTypes: ['first-arrival'],
    endpoints: builder => ({
        organizationFeatures: builder.query<OrganizationFeatures, void>({
            query: () => '/features'
        }),
        getIsFirstArrival: builder.query<boolean, void>({
            queryFn: async (arg, api: BaseQueryApi, extraOptions, baseQuery) => {
                const orgId = (api.getState() as AppState).organizations.currentOrganization?.id;
                if (!orgId) {
                    return { error: { status: 'CUSTOM_ERROR', error: "Not authorized" } };
                }

                const cachedIsFirstArrival = getIsFirstArrival(orgId);
                if (cachedIsFirstArrival !== undefined) {
                    return { data: cachedIsFirstArrival };
                }

                const result = await baseQuery(
                    {
                        url: '/first-arrival',
                        method: 'GET'
                    }) as QueryReturnValue<boolean, FetchBaseQueryError, {}>

                // Only set cached state when out of first arrival, so we check the service until first arrival is complete
                if (result.data === false) {
                    setFirstArrivalComplete(orgId);
                }

                return result;
            },
            providesTags: ['first-arrival']
        })
    })
});

export const selectFirstArrivalState = (state: AppState) => organizationApiSlice.endpoints.getIsFirstArrival.select()(state).data;

const featuresSelector = organizationApiSlice.endpoints.organizationFeatures.select();
export const selectDefaultProjectEnabled = (state: AppState) => !!featuresSelector(state).data?.workspaceDefaultProjectTemplate;
export const selectDiscoveryEnabled = (state: AppState) => !!featuresSelector(state).data?.discovery;
export const mcpWorkflowEnabled = (state: AppState) => !!featuresSelector(state).data?.mcpWorkflow;
export const selectReportBatchSize = (state: AppState) => featuresSelector(state).data?.reportBatchSize ?? 10;  // Default single batch size is 10
export const selectChatDisabled = (state: AppState) => !!featuresSelector(state).data?.disableChat;

export const {
    useOrganizationFeaturesQuery,
    useGetIsFirstArrivalQuery
} = organizationApiSlice;

export const selectDocSetMinFiles = (state: AppState) => organizationApiSlice.endpoints.organizationFeatures.select()(state).data?.docsetMinFiles;

export const completeFirstArrivalThunk = () => async (dispatch: AppDispatch, getState: () => AppState) => {
    const orgId = getState().organizations.currentOrganization?.id;
    if (!!orgId) {
        setFirstArrivalComplete(orgId);
        dispatch(organizationApiSlice.util.invalidateTags(['first-arrival']));
    }
}
